/**
 * ToastService
 * 
 * Service responsible for displaying user feedback messages throughout the MacSafety
 * web application using Ionic toast notifications. Provides consistent styling and
 * behavior for success, warning, and error messages across all components.
 * 
 * Key Features:
 * - Success message notifications with green styling
 * - Warning message notifications with amber/orange styling
 * - Error message notifications with red styling
 * - Consistent positioning (top of screen) and duration (3 seconds)
 * - Ionic toast integration for native mobile feel
 * - Async/await pattern for proper toast lifecycle management
 * 
 * Toast Configuration:
 * - Duration: 3000ms (3 seconds)
 * - Position: Top of screen for maximum visibility
 * - Colors: success (green), warning (amber), danger (red)
 * - Ionic integration for cross-platform compatibility
 * 
 * Components that utilize this service:
 * - All form submission components for user feedback
 * - Service classes for error and success notifications
 * - Form validation components for user guidance
 * - Authentication components for login/logout feedback
 * - Any component requiring user notification capabilities
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  constructor(private toastController: ToastController) {}

  /**
   * Displays a success message toast notification
   * Uses green color scheme and appears at the top of the screen
   * Automatically dismisses after 3 seconds
   * 
   * @param message - The success message to display to the user
   * @returns Promise that resolves when the toast is displayed
   */
  async showSuccessMessage(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message: message,
      duration: 3000,
      color: 'success',
      position: 'top'
    });
    await toast.present();
  }


  /**
   * Displays an info message toast notification
   * Uses blue color scheme and appears at the top of the screen
   * Automatically dismisses after specified duration (default 3 seconds)
   */
  async showInfoMessage(message: string, duration: number = 3000): Promise<void> {
    const toast = await this.toastController.create({
      message: message,
      duration: duration,
      color: 'primary',
      position: 'top'
    });
    await toast.present();
  }

  /**
   * Displays a warning message toast notification
   * Uses amber/orange color scheme and appears at the top of the screen
   * Automatically dismisses after 3 seconds
   * 
   * @param message - The warning message to display to the user
   * @returns Promise that resolves when the toast is displayed
   */
  async showWarningMessage(message: string, duration: number = 3000): Promise<void> {
    const toast = await this.toastController.create({
      message: message,
      duration: duration,
      color: 'warning',
      position: 'top'
    });
    await toast.present();
  }

  /**
   * Displays an error message toast notification
   * Uses red color scheme and appears at the top of the screen
   * Automatically dismisses after 3 seconds
   * 
   * @param message - The error message to display to the user
   * @returns Promise that resolves when the toast is displayed
   */
  async showErrorMessage(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message: message,
      duration: 3000,
      color: 'danger',
      position: 'top'
    });
    await toast.present();
  }
} 